import React, { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { PublicSitterItem, Sitter } from 'us-web-services';
import { AvatarSquare } from '../AvatarSquare';
import { SitterTitle } from '../SitterTitle/SitterTitle';
import { onEnterAction } from '../utils/a11y';
import { HeartButton } from './HeartButton';
import { SitterCardDetails } from './SitterCardDetails';
import { SitterCardSkeleton } from './SitterCardSkeleton';

type Props = {
  containerClasses?: string;
  customWidth?: boolean;
  hideDetails?: boolean;
  hideExperience?: boolean;
  imageSize?: number;
  loading?: boolean;
  numberOfChildren?: number;
  reviewSize?: string;
  sitter: Sitter | PublicSitterItem;
  handleClick(sitterId: number | undefined): void;
  setFavorite?: (isFavorited: boolean, favoriteId: number, sitterId: number) => Promise<void>;
  trackingCallback(event: string, params?: Record<string, unknown>): Promise<void>;
};

export const SitterCard = (
  {
    containerClasses,
    customWidth,
    hideDetails,
    hideExperience,
    imageSize,
    loading,
    numberOfChildren,
    reviewSize,
    sitter,
    handleClick,
    setFavorite,
    trackingCallback,
  }: Props): JSX.Element => {
  const { firstName, pictureUrl, relationship, id } = sitter;
  const { bookmarked, favorite } = relationship || {};
  const { t } = useTranslation();

  const sitterClickHandler = async (sitterIdClick?: number): Promise<void> => {
    const trackingProps = {
      elementId: 'sitter-card',
      sitterId: sitterIdClick,
    };

    await trackingCallback('clicked-button', trackingProps);
    handleClick(sitterIdClick);
  }

  if (loading) {
    return <SitterCardSkeleton />
  }

  const onCardClick = (): Promise<void> => sitterClickHandler(id);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => onEnterAction(e, onCardClick);

  return (
    <div
      aria-label={t('components.sitterCard.ariaLabel', { sitterName: firstName })}
      className={`${customWidth ? '' : 'w-full md:w-[180px]'} rounded-2xl cursor-pointer hover:bg-gray-100` +
        `${containerClasses ? ` ${containerClasses}` : ''}`}
      onClick={onCardClick}
      onKeyDown={onKeyDown}
      role='button'
      tabIndex={0}
    >
      <div className='relative'>
        <div className='absolute bottom-2 left-2'
          onClick={(e): void => e.stopPropagation()}
          onKeyDown={(e): void => e.stopPropagation()}
          tabIndex={-1}
          role='none'
        >
          {setFavorite && <HeartButton
            favoriteId={favorite?.id ?? 0}
            isFavorited={bookmarked}
            sitterId={id ?? 0}
            setFavorite={setFavorite}
            trackingCallback={trackingCallback}
          />}
        </div>
        <AvatarSquare imageUrl={pictureUrl!} size={imageSize} alt='Sitter photo' />
      </div>
      <div className={`px-1 ${hideDetails ? 'py-2' : 'pt-2'}`}>
        <SitterTitle firstName={firstName || ''} />
      </div>
      {!hideDetails && <SitterCardDetails sitter={sitter} reviewSize={reviewSize}
                                          numberOfChildren={numberOfChildren} hideExperience={hideExperience} />}
    </div>
  );
};
